import React, { useEffect, useState } from "react";
import {
  Grid,
  Container,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
// Custom
import CustomAppBar from "../../components/AppBar";
import LoaderContainer from "../../components/LoaderContainer";
import { CustomTitle } from "../../components/Custom/CustomTypography";
import CustomTabs from "../../components/Custom/CustomTabs";
import ListView from "./FPOList";
import SearchComponent from "../../components/Custom/CustomSearch";
// api
import api from "../../globals/axios";
import CustomDashboardCard from "../../components/Custom/CustomDashboard";
import PieChartData from "../../components/Custom/Charts";
import { useTranslation } from "react-i18next";

const AggregatorDashboard = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [fpoList, setFpoList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [fpoCount, setFpoCount] = useState(0);
  const [farmerCount, setFarmerCount] = useState(0);

  const [districts, setDistricts] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState("All");
  const [commodities, setCommodities] = useState([]);
  const [selectedCommodity, setSelectedCommodity] = useState("All");

  const tabTitles = [t("FPOs")];
  const counts = [fpoCount];
  const tabColors = ["#F3FDF3"];
  const tabBordersColor = ["1px solid #4CAF50"];
  const textColors = ["#4CAF50"];

  const fetchFpoList = async () => {
    try {
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (selectedDistrict !== "All") params.district = selectedDistrict;
      if (selectedCommodity !== "All") params.commodity = selectedCommodity;

      const response = await api.get("/fpoaggregator/aggregatorfpo/", {
        params,
      });

      const { results, total_fpos, total_farmers } = response.data;

      setFpoList(results);
      setFpoCount(total_fpos);
      setFarmerCount(total_farmers);

      setLoading(false);
    } catch (error) {
      console.error("Error fetching FPO list:", error);
      setLoading(false);
    }
  };

  const fetchDistrictList = async () => {
    try {
      const response = await api.get("/common/districts/");
      setDistricts(response.data);
    } catch (error) {
      console.error("Error fetching District list:", error);
    }
  };

  const fetchCommodityList = async () => {
    try {
      const response = await api.get("/common/commodities/");
      setCommodities(response.data);
    } catch (error) {
      console.error("Error Fetching Commodities list:", error);
    }
  };

  const handleDistrictChange = (e) => {
    setSelectedDistrict(e.target.value);
  };

  const handleCommodityChange = (e) => {
    setSelectedCommodity(e.target.value);
  };

  const handleSearch = (value) => {
    setSearchQuery(value);
  };

  const pieChartData = {
    labels: [t("TOTAL FPOS"), t("TOTAL FARMERS")],
    datasets: [
      {
        data: [fpoCount, farmerCount],
        backgroundColor: ["#75C178", "#CF5C5C"],
        hoverBackgroundColor: ["#75C178", "#CF5C5C"],
      },
    ],
  };

  useEffect(() => {
    fetchFpoList();
  }, [searchQuery, selectedDistrict, selectedCommodity]);

  useEffect(() => {
    fetchDistrictList();
    fetchCommodityList();
    fetchFpoList();
  }, []);

  return (
    <div>
      <CustomAppBar />
      <LoaderContainer loading={loading}>
        <Container>
          <CustomTitle>{t("Dashboard")}</CustomTitle>

          <Grid container spacing={1}>
            <Grid item xs={6}>
              <FormControl fullWidth sx={{ marginTop: 2 }}>
                <InputLabel id="district-select-label">
                  {t("District")}
                </InputLabel>
                <Select
                  labelId="district-select-label"
                  id="district-select"
                  value={selectedDistrict}
                  label={t("district")}
                  name="district"
                  onChange={handleDistrictChange}
                >
                  <MenuItem value="All">{t("All")}</MenuItem>
                  {districts.map((district) => (
                    <MenuItem key={district.id} value={district.id}>
                      {district.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth sx={{ marginTop: 2 }}>
                <InputLabel id="commodity-select-label">
                  {t("Commodity")}
                </InputLabel>
                <Select
                  labelId="commodity-select-label"
                  id="commodity-select"
                  onChange={handleCommodityChange}
                  value={selectedCommodity}
                  label={t("Commodity")}
                  name="commodity"
                >
                  <MenuItem value="All">{t("All")}</MenuItem>
                  {commodities.map((commodity) => (
                    <MenuItem key={commodity.id} value={commodity.id}>
                      {commodity.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{ marginTop: "20px" }}>
            <Grid container spacing={0.5}>
              <Grid item xs={4}>
                <CustomDashboardCard title={t("Total FPO")} data={fpoCount} />
              </Grid>
              <Grid item xs={4}>
                <CustomDashboardCard
                  title={t("Total Farmers")}
                  data={farmerCount}
                />
              </Grid>
              <Grid item xs={4}>
                <CustomDashboardCard title={t("Coverage Field")} data={0} />
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ marginTop: "20px" }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <PieChartData data={pieChartData} t={t} />
              </Grid>
            </Grid>
          </Box>

          <Box
            sx={{
              border: "1px solid #A5A5A5",
              marginTop: "20px",
            }}
          >
            <Grid container>
              <Grid item xs={12}>
                <CustomTabs
                  numTabs={tabTitles.length}
                  tabTitles={tabTitles}
                  counts={counts}
                  tabColors={tabColors}
                  tabBordersColor={tabBordersColor}
                  textColors={textColors}
                />
              </Grid>

              <Grid item xs={12}>
                <SearchComponent
                  onSearch={handleSearch}
                  title={t("FPOs List")}
                  t={t}
                />
              </Grid>
            </Grid>
            <ListView data={fpoList} />
          </Box>
        </Container>
      </LoaderContainer>
    </div>
  );
};

export default AggregatorDashboard;
